import React, { useEffect, useState, useRef } from 'react';
import { ImageBackground, StyleSheet, View, Image, TouchableOpacity, Platform } from 'react-native';
import { background, greenDot, redDot, Icons } from '../../assets';
import LayoutWrapper from '../../components/LayoutWrapper/LayoutWrapper';
import { Styles } from '../../styles';
import TextTitle from '../../components/Text/TextTitle';
import { useGlobalState } from '../../shared/state';
import { I18N } from '../../shared/util';
import { useNavigation } from '@react-navigation/native';
import _ from 'lodash';
import BirkenstockLabel from '../../components/BirkenstockLabel';
import useBirkenstockPrint from './useBirkenstockPrint';

const NewScan: React.FC<any> = (): JSX.Element => {
  const [, setCurrentScreen] = useGlobalState('currentScreen');
  const [currentUser, setCurrentUser] = useGlobalState('currentUser');
  const [, setCurrentMeasurements] = useGlobalState('currentMeasurements');
  const [, setMatchedProducts] = useGlobalState('matchedProducts');
  const [, setInitialMatchedProducts] = useGlobalState("initialMatchedProducts");
  const [, setAllProducts] = useGlobalState('allProducts');
  const [currentScan, setCurrentScan] = useGlobalState('currentScan');
  const [userToken, setUserToken] = useGlobalState('userToken');
  const [currentScanner] = useGlobalState("currentScanner");
  const [language] = useGlobalState('language');
  const [Colors] = useGlobalState('colors');
  const [carts, setCarts] = useGlobalState('carts');

  const [userInfo, setUserInfo] = useState<string | undefined>(undefined);

  const i18n = I18N(language);
  const navigation = useNavigation();

  const logout = (redirectToHome?: boolean) => {
    setCurrentUser(undefined);
    setUserToken(undefined);
    setCurrentMeasurements(undefined);
    setMatchedProducts(undefined);
    setInitialMatchedProducts(undefined);
    setCurrentScan(undefined);
    setAllProducts([]);
    setCarts([]);
    setCurrentScreen('Home');
    redirectToHome && navigation.navigate('Home');
  };

  useEffect(() => {
    setUserInfo(userToken
      ? currentUser.firstName.length > 0 && currentUser.lastName.length > 0
        ? `${currentUser.firstName} ${currentUser.lastName}`
        : currentUser.email
      : currentUser && currentUser.employeeNumber
        ? currentUser.employeeNumber
        : i18n.t('new user'))
    if (currentScanner && currentScanner.partner && currentScanner.partner?.toLowerCase() === "ssv" && currentUser) setUserInfo(`${currentUser.firstName} ${currentUser.lastName}`);

  }, [])

  const handleNewScanButton = () => {
    if (currentScan || currentUser) {
      logout();
      navigation.navigate('NewScanGenderSelection', { resetState: true });
    }
  }

  const [birkenstockSummary] = useGlobalState('birkenstockSummary');

  const printRef = useRef<HTMLDivElement>(null);

  const handlePrint = useBirkenstockPrint(printRef);

  return (
    <ImageBackground source={background} style={Styles.backgroundImage} imageStyle={{ opacity: 0.4 }}>
      <LayoutWrapper showHeaderCloseButton showHeaderLogo showBackButton={false}>
        <View style={styles.container}>
          <View style={styles.content}>
            <View style={styles.iconsContainer}>
              <Image style={styles.DotWrapper} source={currentUser ? greenDot : redDot} />
              <Image style={styles.iconWrapper} source={Icons.loginIconOutlined} />
            </View>
            <TextTitle text={userInfo} fontFamily="Montserrat-Regular" fontSize={15} fontWeight="normal" uppercase={false} />
            <View style={styles.divider} />
            <TextTitle
              text={currentUser ? i18n.t('new scan modal logged in user') : i18n.t('new scan modal not logged in user')}
              fontFamily="Montserrat-Regular"
              fontSize={14}
              fontWeight="normal"
              uppercase={false}
            />
            {birkenstockSummary && (
              <View style={{ alignItems: 'center' }}>
                <TouchableOpacity
                  style={[Styles.submitButtonLight, { marginTop: 10, backgroundColor: '#001689', borderColor: '#001689' }]}
                  onPress={handlePrint}
                >
                  <TextTitle fontSize={14} fontWeight="bold" text="Print Member Card" color="white" />
                </TouchableOpacity>
                <View pointerEvents="none" style={styles.printBuffer}>
                  {/* Hidden in UI – used only for printing */}
                  <div id="print-birkenstock-label" ref={printRef} style={{ position: 'absolute', top: -9999, left: -9999 }}>
                    <BirkenstockLabel summary={birkenstockSummary} />
                  </div>
                </View>
              </View>
            )}
            {!_.isEmpty(carts) && (
              <>
                <TextTitle text={i18n.t("itemsInCartWarning")} color="red" fontFamily="Montserrat-Regular" fontSize={15} fontWeight="normal" uppercase={false} />
                <TouchableOpacity style={[Styles.submitButtonLight, { backgroundColor: Colors.mainColorDark.color, width: 145, height: 35, borderRadius: 13, top: "1%" }]} onPress={() => navigation.navigate("CartModal")}>
                  <TextTitle fontSize={11} fontWeight="normal" text={i18n.t('cartButton')} color="white" />
                </TouchableOpacity>
              </>
            )}
            <TouchableOpacity
              style={[
                Styles.submitButtonDark,
                {
                  backgroundColor: Colors.mainColorLight.color,
                  width: 250,
                  height: 55,
                  borderRadius: 23,
                },
              ]}
              onPress={() => {
                if (currentScanner && currentScanner.partner?.toLowerCase() === "omr") {
                  window.location.replace('https://go.emarsys.com/puma-gewinnspiel-qr.html');
                } else {
                  handleNewScanButton();
                }
              }}
            >
              <TextTitle
                fontSize={18}
                fontWeight="normal"
                text={i18n.t('new scan')}
                color="white"
              />
            </TouchableOpacity>
            <TouchableOpacity style={[Styles.submitButtonLight, { width: 145, height: 35, borderRadius: 13 }]} onPress={() => navigation.navigate('NewScanGenderSelection', { resetState: true })}>
              <TextTitle fontSize={11} fontWeight="normal" text={i18n.t('repeatScanButton')} />
            </TouchableOpacity>
            <TouchableOpacity style={[Styles.submitButtonLight, { width: 145, height: 35, borderRadius: 13 }]} onPress={() => (currentUser ? logout(true) : Platform.OS === 'web' ? navigation.replace('Register') : navigation.replace('RegisterModal'))}>
              <TextTitle fontSize={11} fontWeight="normal" text={currentUser ? i18n.t('auth.logOut') : i18n.t('save result')} />
            </TouchableOpacity>
          </View>
        </View>
      </LayoutWrapper>
    </ImageBackground>
  );
};

export default NewScan;

const styles = StyleSheet.create({
  container: {
    width: '70%',
    height: '90%',
    alignSelf: 'center',
    backgroundColor: 'white',
    alignItems: 'center',
    justifyContent: 'center'
  },
  content: {
    width: '50%',
    height: '80%',
    justifyContent: 'space-evenly',
    alignSelf: 'center',
  },
  iconsContainer: {
    width: '40%',
    height: '25%',
    alignSelf: 'center',
  },
  DotWrapper: {
    alignSelf: 'flex-end',
    width: '12%',
    height: '12%',
    marginRight: '10%',
    resizeMode: 'contain',
  },
  iconWrapper: {
    width: '75%',
    height: '70%',
    resizeMode: 'contain',
    alignSelf: 'center',
  },
  divider: {
    width: '60%',
    alignSelf: 'center',
    borderTopColor: '#707070',
    borderTopWidth: 1,
  },
  printBuffer: {
    width: 0,
    height: 0,
    overflow: 'hidden',
  },
});
